import { generateUUIDv4 } from "src/pages/helpers";

const COOKIE_NAME = "inhouse_anonymous_id";
const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || "inhouse.ai";
const IFRAME_URL = `https://www.${BASE_DOMAIN}/set-id`;
const POLL_INTERVAL_MS = 100;
const MAX_POLL_ATTEMPTS = 30; // 3 seconds total

/**
 * Get inhouse_anonymous_id from cookie
 */
export const getInhouseAnonymousIdFromCookie = () => {
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [name, value] = cookie.split("=");
    if (name === COOKIE_NAME && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
};

/**
 * Set inhouse_anonymous_id cookie
 */
export const setInhouseAnonymousIdCookie = (id, days = 365) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const domain = window.location.hostname.includes(BASE_DOMAIN) ? `.${BASE_DOMAIN}` : window.location.hostname;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(id)}; expires=${date.toUTCString()}; path=/; domain=${domain}; SameSite=Lax`;
};

/**
 * Create hidden iframe to trigger cookie setting
 */
const createAnonymousIdIframe = () => {
  const existingIframe = document.getElementById("inhouse-anonymous-id-iframe");
  if (existingIframe) {
    return existingIframe;
  }

  const iframe = document.createElement("iframe");
  iframe.src = IFRAME_URL;
  iframe.style.cssText = "display:none;width:0;height:0;border:0;";
  iframe.id = "inhouse-anonymous-id-iframe";
  document.body.appendChild(iframe);
  return iframe;
};

/**
 * Remove the anonymous ID iframe
 */
const removeAnonymousIdIframe = () => {
  const iframe = document.getElementById("inhouse-anonymous-id-iframe");
  if (iframe) {
    iframe.remove();
  }
};

/**
 * Poll for cookie availability
 */
const pollForCookie = () => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const check = () => {
      const id = getInhouseAnonymousIdFromCookie();
      if (id) {
        resolve(id);
        return;
      }

      attempts++;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        reject(new Error("Timeout waiting for inhouse_anonymous_id cookie"));
        return;
      }

      setTimeout(check, POLL_INTERVAL_MS);
    };

    check();
  });
};

/**
 * Main function to get inhouse anonymous ID
 * Replaces getSegmentId()
 */
export const getInhouseAnonymousId = async () => {
  // 1. Check if cookie already exists
  const existingId = getInhouseAnonymousIdFromCookie();
  if (existingId) {
    return existingId;
  }

  // 2. Create hidden iframe to set cookie
  createAnonymousIdIframe();

  try {
    // 3. Poll for cookie
    const id = await pollForCookie();

    // 4. Cleanup iframe
    removeAnonymousIdIframe();

    return id;
  } catch (error) {
    // 5. Cleanup on timeout
    removeAnonymousIdIframe();

    // 6. Fallback: generate UUID locally
    console.warn("Failed to get inhouse_anonymous_id from iframe, generating locally:", error.message);
    const fallbackId = generateUUIDv4();
    setInhouseAnonymousIdCookie(fallbackId);
    return fallbackId;
  }
};

/**
 * Clear the anonymous ID cookie (for logout)
 */
export const clearInhouseAnonymousId = () => {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${BASE_DOMAIN}`;
};

/**
 * Regenerate inhouse_anonymous_id via the cross-domain iframe flow.
 * Clears the old cookie, loads /set-id?force=true in an iframe so the
 * website domain sets a fresh cookie, then polls until it appears.
 * Falls back to local generation if the iframe times out.
 * Used when the backend reports AnonymousIDBelongsToSignedUpUserException.
 */
export const regenerateInhouseAnonymousId = async () => {
  clearInhouseAnonymousId();

  removeAnonymousIdIframe();

  const iframe = document.createElement("iframe");
  iframe.src = `${IFRAME_URL}?force=true`;
  iframe.style.cssText = "display:none;width:0;height:0;border:0;";
  iframe.id = "inhouse-anonymous-id-iframe";
  document.body.appendChild(iframe);

  try {
    const newId = await pollForCookie();
    removeAnonymousIdIframe();
    return newId;
  } catch (error) {
    removeAnonymousIdIframe();
    console.warn("Iframe regeneration failed, falling back to local generation:", error.message);
    const fallbackId = generateUUIDv4();
    setInhouseAnonymousIdCookie(fallbackId);
    return fallbackId;
  }
};
